import {
  Alert,
  AlertTitle,
  Box,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  StackDivider,
  VStack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { FilterIcon, SearchIcon } from 'components/icons';
import { OverlayProps } from 'components/TaskOverlay';
import React, { useEffect, useState } from 'react';
import SearchField from 'components/SearchField';
import { SearchResults } from './components/SearchResults';
import { useForm } from 'react-hook-form';
import { AdvancedSearchOptions } from './components/AdvancedSearchOptions';
import { useFeatureFlag } from 'utils/sessionProvider';
import { SearchTypeFilter } from './components/SearchResults/__generated__/searchQuery.graphql';
import { TypeFilter } from './components/TypeFilter';
import { useSearchModal } from './useSearchModal';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import { useLocalStorage } from 'hooks/useBrowserStorage';

export const SearchModal = () => {
  const { isOpen, closeSearch: onClose, currentQuery } = useSearchModal();
  const { register, watch, resetField } = useForm({
    defaultValues: { query: currentQuery || '', spaceIds: [] as string[] },
  });
  const [showAdvanced, setAdvanced] = useLocalStorage('advancedSearch', false);
  const [types, setTypes] = useState<SearchTypeFilter[]>([]);
  const searchEnabled = useFeatureFlag('SYSTEM_SEARCH');

  const value = watch('query');
  const spaceIds = watch('spaceIds');

  const isDesktop = useActiveLayout() === Layout.DESKTOP;

  useEffect(() => {
    resetField('query', { defaultValue: currentQuery });
  }, [currentQuery]);

  if (!searchEnabled) {
    return <NoSearchModal isOpen={isOpen} onClose={onClose} />;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent data-qa="search-modal">
        <ModalHeader>
          <HStack>
            <SearchField
              size="md"
              placeholder={t`Search`}
              {...register('query')}
            />
            <IconButton
              aria-label={t`Filter`}
              icon={<FilterIcon />}
              onClick={() => setAdvanced((s) => !s)}
              isActive={showAdvanced}
            />
          </HStack>
        </ModalHeader>
        {(value || showAdvanced) && (
          <ModalBody pb={4}>
            <VStack
              align="start"
              spacing={3}
              divider={isDesktop ? <StackDivider /> : undefined}
            >
              <Stack
                direction={{ base: 'column', md: 'row' }}
                w="100%"
                divider={<StackDivider />}
                spacing={isDesktop ? 10 : 4}
              >
                <Box flex={1} data-qa="search-modal-results">
                  <SearchResults
                    query={value}
                    spaceIds={spaceIds}
                    types={types}
                  />
                </Box>
                {showAdvanced && (
                  <Box flex={0.5}>
                    <AdvancedSearchOptions register={register} />
                  </Box>
                )}
              </Stack>
              {showAdvanced && (
                <TypeFilter activeTypes={types} onFilterChanged={setTypes} />
              )}
            </VStack>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  );
};

const NoSearchModal = ({ isOpen, onClose }: OverlayProps) => (
  <Modal isOpen={isOpen} onClose={onClose} size="4xl" data-qa="search-modal">
    <ModalOverlay />
    <ModalContent>
      <ModalHeader>
        {t`Search is disabled`}
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody pb={4}>
        <Alert
          status="info"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          borderRadius="md"
        >
          <SearchIcon size="3x" style={{ padding: 8 }} />
          <AlertTitle>
            {t`The search system is currently not available.`}
          </AlertTitle>
        </Alert>
      </ModalBody>
    </ModalContent>
  </Modal>
);

export default SearchModal;
